import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import shield from '../assets/shortley-shield.png';
import Icon from './ui/Icons';
import { CailynMark } from './Cailyn';
import { useDarkMode } from '../context/DarkModeContext';
import { getTheme } from '../theme';

const NAV_ITEMS = [
  {label:'Dashboard',icon:'home',path:'/dashboard'},
  {label:'AI Tutor',icon:'cailyn',path:'/tutor'},
  {label:'Cailyn Live',icon:'mic',path:'/live'},
  {label:'Review Queue',icon:'repeat',path:'/review'},
  {label:'Question Rush',icon:'bolt',path:'/rush'},
  {label:'Flashcards',icon:'layers',path:'/flashcards'},
  {label:'Study Planner',icon:'calendar',path:'/planner'},
  {label:'Past Papers',icon:'fileText',path:'/past-papers'},
  {label:'Exam Simulator',icon:'test',path:'/exam'},
  {label:'Test Prep',icon:'sparkle',path:'/test-prep'},
  {label:'Score Tracker',icon:'bars',path:'/scores'},
  {label:'Notes',icon:'book',path:'/notes'},
  {label:'Analytics',icon:'trendUp',path:'/analytics'},
  {label:'Focus Room',icon:'clock',path:'/focus'},
  {label:'Community',icon:'globe',path:'/community'},
  {label:'My Profile',icon:'user',path:'/profile'},
  {label:'Settings',icon:'settings',path:'/settings'},
  {label:'Help',icon:'help',path:'/help'},
  {label:'Pricing & Pro',icon:'sparkle',path:'/pricing'},
];

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { credits, isPro } = useCredits();
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const gold = t.gold;

  if (!user) return null;

  return (
    <>
      <div className="mobile-nav-spacer" style={{display:'none',height:'54px',flexShrink:0}}></div>

      {/* Mobile top bar */}
      <div className="mobile-nav" style={{display:'none',position:'fixed',top:0,left:0,right:0,height:'54px',background:t.bg,borderBottom:`1px solid ${t.borderSoft}`,backdropFilter:'blur(20px)',zIndex:100,alignItems:'center',justifyContent:'space-between',padding:'0 16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <img src={shield} alt="" style={{width:'26px',height:'26px',objectFit:'contain'}} />
          <span style={{fontSize:'15px',fontWeight:'700',color:t.text,letterSpacing:'-0.3px',fontFamily:"'DM Serif Display', Georgia, serif"}}>Learnable</span>
        </div>
        <button onClick={()=>setOpen(true)} aria-label="Open menu" style={{background:'none',border:'none',color:t.textTert,cursor:'pointer',padding:'4px 8px',display:'flex',alignItems:'center'}}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
        </button>
      </div>

      {/* Drawer overlay */}
      {open && (
        <div style={{position:'fixed',inset:0,zIndex:200,display:'flex'}}>
          <div onClick={()=>setOpen(false)} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)'}}></div>
          <div style={{position:'relative',width:'280px',height:'100vh',background:t.bg,borderRight:`1px solid ${t.borderSoft}`,display:'flex',flexDirection:'column',overflowY:'auto',zIndex:1}}>
            {/* Header */}
            <div style={{padding:'16px',borderBottom:`1px solid ${t.borderSoft}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <img src={shield} alt="" style={{width:'28px',height:'28px',objectFit:'contain'}} />
                <span style={{fontSize:'16px',fontWeight:'700',color:t.text,fontFamily:"'DM Serif Display', Georgia, serif"}}>Learnable</span>
              </div>
              <button onClick={()=>setOpen(false)} aria-label="Close menu" style={{background:t.borderSoft,border:'none',color:'rgba(245,240,232,0.4)',cursor:'pointer',display:'flex',width:28,height:28,alignItems:'center',justifyContent:'center',borderRadius:6}}>
                <Icon name="x" size={14} />
              </button>
            </div>

            {/* Credits indicator */}
            <div style={{padding:'10px 12px',borderBottom:`1px solid ${t.borderSoft}`}}>
              {isPro ? (
                <div style={{padding:'8px 12px',background:'rgba(61,186,110,0.08)',border:'1px solid rgba(61,186,110,0.2)',borderRadius:'10px',display:'flex',alignItems:'center',gap:'8px'}}>
                  <Icon name="sparkle" size={14} color={t.green} />
                  <div><div style={{fontSize:'12px',fontWeight:'700',color:t.green}}>Pro Plan</div><div style={{fontSize:'11px',color:t.textTert}}>Unlimited AI access</div></div>
                </div>
              ) : (
                <div onClick={()=>{navigate('/pricing');setOpen(false);}} style={{padding:'8px 12px',background:'rgba(212,168,67,0.06)',border:'1px solid rgba(212,168,67,0.2)',borderRadius:'10px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div><div style={{fontSize:'12px',fontWeight:'700',color:gold}}>Free Plan</div><div style={{fontSize:'11px',color:'rgba(245,240,232,0.35)'}}>{credits ?? 50} AI credits left</div></div>
                  <span style={{fontSize:'11px',fontWeight:'700',color:gold}}>Upgrade →</span>
                </div>
              )}
            </div>

            {/* Nav items */}
            <div style={{flex:1,padding:'8px',overflowY:'auto'}}>
              {NAV_ITEMS.map(item=>{
                const active = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <button key={item.path} onClick={()=>{navigate(item.path);setOpen(false);}}
                    style={{width:'100%',display:'flex',alignItems:'center',gap:'12px',padding:'10px 12px',borderRadius:'8px',marginBottom:'1px',border:'none',cursor:'pointer',textAlign:'left',background:active?'rgba(212,168,67,0.08)':'transparent',borderLeft:active?`3px solid ${gold}`:'3px solid transparent',transition:'all 0.12s',height:44}}>
                    {item.icon === 'cailyn'
                      ? <span style={{fontSize:16,color:active?gold:'rgba(245,240,232,0.35)',lineHeight:1}}>✦</span>
                      : <Icon name={item.icon} size={17} color={active?gold:'rgba(245,240,232,0.35)'} />
                    }
                    <span style={{fontSize:'13px',fontWeight:active?'600':'400',color:active?'rgba(245,240,232,0.9)':'rgba(245,240,232,0.45)'}}>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div style={{padding:'12px',borderTop:`1px solid ${t.borderSoft}`}}>
              <button onClick={()=>{logout();setOpen(false);}} style={{width:'100%',padding:'10px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',background:t.surface2,color:t.textTert,border:`1px solid ${t.borderSoft}`,cursor:'pointer'}}>Sign out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
